import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WebhookDeliveriesTable } from "@/components/WebhookDeliveriesTable";
import type { components } from "@dolfin/api-admin";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type WebhookDeliveryStatus = NonNullable<components["schemas"]["WebhookDeliveryStatus"]>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LIMIT = 100;

const statusOptions: { value: WebhookDeliveryStatus | "any"; label: string }[] = [
  { value: "any", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Delivered", label: "Delivered" },
  { value: "Failed", label: "Failed" },
];

const sinceOptions: { value: string; label: string }[] = [
  { value: "1", label: "Last 24 hours" },
  { value: "3", label: "Last 3 days" },
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
];

function parseSinceDays(raw: string | null): number {
  const n = Number(raw);
  return sinceOptions.some((o) => o.value === raw) && Number.isFinite(n) ? n : 3;
}

export function WebhookDeliveriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [clientIdInput, setClientIdInput] = useState(searchParams.get("clientId") ?? "");
  const debouncedClientId = useDebouncedValue(clientIdInput.trim());

  const statusParam = searchParams.get("status");
  const status: WebhookDeliveryStatus | "any" = statusOptions.some(
    (o) => o.value === statusParam,
  )
    ? (statusParam as WebhookDeliveryStatus | "any")
    : "any";

  const sinceDays = parseSinceDays(searchParams.get("sinceDays"));

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setSearchParams(
      (params) => {
        if (debouncedClientId) {
          params.set("clientId", debouncedClientId);
        } else {
          params.delete("clientId");
        }
        return params;
      },
      { replace: true },
    );
  }, [debouncedClientId, setSearchParams]);

  function updateParam(key: string, value: string | null) {
    setSearchParams(
      (params) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
        return params;
      },
      { replace: true },
    );
  }

  // Only apply the client filter once it's a full UUID — the API expects uuid format.
  const clientId = UUID_PATTERN.test(debouncedClientId) ? debouncedClientId : undefined;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-1 text-2xl font-bold">Webhook Deliveries</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Cross-client delivery feed for ops monitoring. Showing up to {LIMIT} deliveries.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by client ID…"
          value={clientIdInput}
          onChange={(e) => setClientIdInput(e.target.value)}
          aria-invalid={!!debouncedClientId && !clientId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
        <Select
          value={status}
          onValueChange={(v) => updateParam("status", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={String(sinceDays)}
          onValueChange={(v) => updateParam("sinceDays", v === "3" ? null : v)}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sinceOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
          <RefreshCw className="mr-2 size-4" />
          Refresh
        </Button>
      </div>

      <WebhookDeliveriesTable
        clientId={clientId}
        status={status === "any" ? undefined : status}
        sinceDays={sinceDays}
        limit={LIMIT}
        refreshKey={refreshKey}
      />
    </div>
  );
}
